import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "../lib/apiClient";
import { useI18n } from "../i18n/I18nContext";
import { toast } from "sonner";
import {
  LayoutDashboard, Network, Globe, Server, Wifi, Shield, Cpu, TerminalSquare,
  ArrowLeft, RefreshCw, Power, Plus, Trash2, ChevronRight, Radio, HardDrive, Save, Terminal,
} from "lucide-react";

const MENU = [
  { key: "dashboard", icon: LayoutDashboard },
  { key: "interfaces", icon: Network },
  { key: "addresses", icon: Globe },
  { key: "dhcp", icon: Server },
  { key: "hotspot", icon: Wifi },
  { key: "radius", icon: Radio },
  { key: "firewall", icon: Shield },
  { key: "system", icon: Cpu },
  { key: "terminal", icon: TerminalSquare },
];

function Bar({ pct, color = "#2563EB" }) {
  return (
    <div className="h-2 rounded-full bg-[#0B0F17] overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function RouterDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [section, setSection] = useState("dashboard");
  const [addr, setAddr] = useState({ address: "", interface: "bridge-hotspot" });
  const [fw, setFw] = useState({ chain: "forward", action: "accept", comment: "" });
  const [ident, setIdent] = useState("");
  const [term, setTerm] = useState([]);
  const [cmd, setCmd] = useState("");
  const termRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/routers/${id}/device`);
      setD(data); setIdent(data.system.identity);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); nav("/routers"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { if (termRef.current) termRef.current.scrollTop = termRef.current.scrollHeight; }, [term]);

  if (!d) return <div className="min-h-screen flex items-center justify-center text-slate-400">{t("common.loading")}</div>;

  const act = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const toggleIf = (n) => act(() => api.post(`/routers/${id}/device/interface/${n}/toggle`));
  const addAddr = () => { if (!addr.address) return; act(() => api.post(`/routers/${id}/device/address`, addr), "✓").then(() => setAddr({ address: "", interface: "bridge-hotspot" })); };
  const delAddr = (aid) => act(() => api.delete(`/routers/${id}/device/address/${aid}`));
  const addFw = () => act(() => api.post(`/routers/${id}/device/firewall`, fw), "✓").then(() => setFw({ chain: "forward", action: "accept", comment: "" }));
  const toggleFw = (fid) => act(() => api.post(`/routers/${id}/device/firewall/${fid}/toggle`));
  const delFw = (fid) => act(() => api.delete(`/routers/${id}/device/firewall/${fid}`));
  const saveIdent = () => act(() => api.put(`/routers/${id}/device/identity`, { identity: ident }), "✓");
  const runCmd = async () => {
    if (!cmd.trim()) return;
    try {
      const { data } = await api.post(`/routers/${id}/device/terminal`, { command: cmd });
      setTerm((p) => [...p, { p: data.prompt, o: data.output }]);
      setCmd("");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const mem = d.system.total_memory || 1;
  const memPct = Math.round((d.system.used_memory / mem) * 100);

  return (
    <div className="min-h-screen flex bg-[#0B0F17]" data-testid="router-detail-page">
      {/* WinBox-style sidebar */}
      <aside className="w-56 shrink-0 border-r border-[#1F2937] bg-[#0d121c] flex flex-col">
        <div className="h-14 flex items-center gap-2 px-4 border-b border-[#1F2937]">
          <div className="h-8 w-8 rounded-md bg-cyan-600 flex items-center justify-center"><HardDrive className="h-4 w-4 text-white" /></div>
          <div className="leading-tight min-w-0">
            <div className="font-display font-bold text-xs truncate">{d.router.name}</div>
            <div className="text-[10px] font-mono text-slate-500 truncate">{d.router.host}</div>
          </div>
        </div>
        <nav className="flex-1 py-2">
          {MENU.map((m) => (
            <button key={m.key} data-testid={`wb-menu-${m.key}`} onClick={() => setSection(m.key)}
              className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${section === m.key ? "bg-cyan-600/15 text-cyan-300 border-r-2 border-cyan-400" : "text-slate-400 hover:bg-white/5 hover:text-slate-100"}`}>
              <m.icon className="h-4 w-4" /> {t(`wb.${m.key}`)}
            </button>
          ))}
        </nav>
        <button data-testid="wb-back" onClick={() => nav("/routers")} className="flex items-center gap-2 px-4 py-3 text-sm text-slate-400 hover:text-slate-100 border-t border-[#1F2937]">
          <ArrowLeft className="h-4 w-4" /> {t("wb.back")}
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 flex items-center justify-between px-6 border-b border-[#1F2937] bg-[#0B0F17]/80 backdrop-blur">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-display font-semibold">{t("wb.title")}</span>
            <ChevronRight className="h-4 w-4 text-slate-600" />
            <span className="text-slate-400">{t(`wb.${section}`)}</span>
            <span className={`ml-3 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${d.router.status === "online" ? "bg-emerald-500/15 text-emerald-400 ring-emerald-500/30" : "bg-rose-500/15 text-rose-400 ring-rose-500/30"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${d.router.status === "online" ? "bg-emerald-400" : "bg-rose-400"}`} /> {d.router.status}
            </span>
          </div>
          <button data-testid="wb-refresh" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium"><RefreshCw className="h-3.5 w-3.5" /> {t("wb.refresh")}</button>
        </header>

        <main className="flex-1 p-6 overflow-y-auto">
          {section === "dashboard" && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Card title={t("wb.cpu")}><div className="font-display text-3xl font-bold">{d.system.cpu_load}%</div><div className="mt-2"><Bar pct={d.system.cpu_load} color="#06B6D4" /></div></Card>
              <Card title={t("wb.memory")}><div className="font-display text-xl font-bold">{(d.system.used_memory/1048576).toFixed(0)} / {(mem/1048576).toFixed(0)} MB</div><div className="mt-2"><Bar pct={memPct} color="#2563EB" /></div></Card>
              <Card title={t("wb.uptime")}><div className="font-display text-2xl font-bold">{d.system.uptime}</div></Card>
              <Card title={t("wb.version")}><div className="font-display text-lg font-bold">RouterOS {d.system.version}</div><div className="text-xs text-slate-500 mt-1">{d.system.board_name}</div></Card>
              <Card title={t("wb.interfaces")}><div className="font-display text-3xl font-bold">{d.interfaces.filter((i)=>i.running).length}<span className="text-slate-500 text-lg">/{d.interfaces.length}</span></div><div className="text-xs text-slate-500 mt-1">{t("wb.running")}</div></Card>
              <Card title={t("wb.addresses")}><div className="font-display text-3xl font-bold">{d.addresses.length}</div></Card>
              <Card title={t("wb.hotspot")}><div className="font-display text-3xl font-bold text-emerald-400">{d.hotspot_active.length}</div></Card>
              <Card title={t("wb.firewall")}><div className="font-display text-3xl font-bold">{d.firewall.filter((f)=>f.enabled).length}<span className="text-slate-500 text-lg">/{d.firewall.length}</span></div></Card>
            </div>
          )}

          {section === "interfaces" && (
            <WBTable headers={["", "Name", "Type", "MAC", "RX MB", "TX MB", ""]}>
              {d.interfaces.map((i) => (
                <tr key={i.name} data-testid={`wb-iface-${i.name}`} className="border-b border-[#1F2937]/60">
                  <td className="px-3 py-2"><span className={`h-2 w-2 rounded-full inline-block ${i.running ? "bg-emerald-400" : "bg-slate-600"}`} /></td>
                  <td className="px-3 py-2 font-mono text-cyan-400">{i.name}<div className="text-[10px] text-slate-500">{i.comment}</div></td>
                  <td className="px-3 py-2 text-slate-400">{i.type}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{i.mac}</td>
                  <td className="px-3 py-2 font-mono">{i.rx_mb}</td>
                  <td className="px-3 py-2 font-mono">{i.tx_mb}</td>
                  <td className="px-3 py-2 text-right">
                    <button data-testid={`wb-iface-toggle-${i.name}`} onClick={() => toggleIf(i.name)} className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs ${i.enabled ? "text-rose-400 hover:bg-rose-500/10" : "text-emerald-400 hover:bg-emerald-500/10"}`}>
                      <Power className="h-3.5 w-3.5" /> {i.enabled ? t("wb.disable") : t("wb.enable")}
                    </button>
                  </td>
                </tr>
              ))}
            </WBTable>
          )}

          {section === "addresses" && (
            <div>
              <div className="flex flex-wrap gap-2 mb-4">
                <input data-testid="wb-addr-input" value={addr.address} onChange={(e) => setAddr({ ...addr, address: e.target.value })} placeholder="10.5.60.1/24" className="rounded-lg bg-[#111827] border border-[#1F2937] px-3 py-2 text-sm font-mono" />
                <input data-testid="wb-addr-iface" value={addr.interface} onChange={(e) => setAddr({ ...addr, interface: e.target.value })} placeholder="interface" className="rounded-lg bg-[#111827] border border-[#1F2937] px-3 py-2 text-sm font-mono" />
                <button data-testid="wb-addr-add" onClick={addAddr} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 px-3 py-2 text-sm font-semibold"><Plus className="h-4 w-4" /> {t("wb.add")}</button>
              </div>
              <WBTable headers={["Address", "Network", "Interface", ""]}>
                {d.addresses.map((a) => (
                  <tr key={a.id} className="border-b border-[#1F2937]/60">
                    <td className="px-3 py-2 font-mono text-cyan-400">{a.address}</td>
                    <td className="px-3 py-2 font-mono text-slate-500">{a.network}</td>
                    <td className="px-3 py-2 text-slate-400">{a.interface}</td>
                    <td className="px-3 py-2 text-right"><button data-testid={`wb-addr-del-${a.id}`} onClick={() => delAddr(a.id)} className="p-1.5 rounded-md hover:bg-rose-500/10 text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button></td>
                  </tr>
                ))}
              </WBTable>
            </div>
          )}

          {section === "dhcp" && (
            <WBTable headers={["Host", "Address", "MAC", "Server"]}>
              {d.dhcp_leases.map((l, i) => (
                <tr key={i} className="border-b border-[#1F2937]/60">
                  <td className="px-3 py-2 font-mono text-cyan-400">{l.host}</td>
                  <td className="px-3 py-2 font-mono">{l.address}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{l.mac}</td>
                  <td className="px-3 py-2 text-slate-400">{l.server}</td>
                </tr>
              ))}
              {d.dhcp_leases.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-500">{t("common.none")}</td></tr>}
            </WBTable>
          )}

          {section === "hotspot" && (
            <WBTable headers={["User", "Address", "MAC", "Uptime", "RX MB", "TX MB"]}>
              {d.hotspot_active.map((h, i) => (
                <tr key={i} data-testid={`wb-hs-${i}`} className="border-b border-[#1F2937]/60">
                  <td className="px-3 py-2 font-mono text-cyan-400">{h.user}</td>
                  <td className="px-3 py-2 font-mono">{h.address}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{h.mac}</td>
                  <td className="px-3 py-2">{h.uptime}</td>
                  <td className="px-3 py-2 font-mono">{h.rx_mb}</td>
                  <td className="px-3 py-2 font-mono">{h.tx_mb}</td>
                </tr>
              ))}
              {d.hotspot_active.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">{t("common.none")}</td></tr>}
            </WBTable>
          )}

          {section === "radius" && (
            <div className="max-w-lg space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Info label="Host" value={d.radius.host} />
                <Info label={t("routers.shared_secret")} value={d.radius.secret_masked} />
                <Info label="Auth Port" value={d.radius.auth_port} />
                <Info label="Acct Port" value={d.radius.acct_port} />
              </div>
              <div className="rounded-lg bg-amber-500/10 ring-1 ring-amber-500/20 px-3 py-2 text-xs text-amber-300">{t("wb.secret_note")}</div>
            </div>
          )}

          {section === "firewall" && (
            <div>
              <div className="flex flex-wrap gap-2 mb-4">
                <select data-testid="wb-fw-chain" value={fw.chain} onChange={(e) => setFw({ ...fw, chain: e.target.value })} className="rounded-lg bg-[#111827] border border-[#1F2937] px-3 py-2 text-sm font-mono">
                  {["input", "forward", "output", "srcnat", "dstnat"].map((c) => <option key={c}>{c}</option>)}
                </select>
                <select data-testid="wb-fw-action" value={fw.action} onChange={(e) => setFw({ ...fw, action: e.target.value })} className="rounded-lg bg-[#111827] border border-[#1F2937] px-3 py-2 text-sm font-mono">
                  {["accept", "drop", "reject", "masquerade", "log"].map((a) => <option key={a}>{a}</option>)}
                </select>
                <input data-testid="wb-fw-comment" value={fw.comment} onChange={(e) => setFw({ ...fw, comment: e.target.value })} placeholder={t("wb.comment")} className="flex-1 min-w-[140px] rounded-lg bg-[#111827] border border-[#1F2937] px-3 py-2 text-sm" />
                <button data-testid="wb-fw-add" onClick={addFw} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 px-3 py-2 text-sm font-semibold"><Plus className="h-4 w-4" /> {t("wb.add")}</button>
              </div>
              <WBTable headers={["", t("wb.chain"), t("wb.action"), t("wb.comment"), ""]}>
                {d.firewall.map((f) => (
                  <tr key={f.id} className="border-b border-[#1F2937]/60">
                    <td className="px-3 py-2"><button data-testid={`wb-fw-toggle-${f.id}`} onClick={() => toggleFw(f.id)}><span className={`h-2 w-2 rounded-full inline-block ${f.enabled ? "bg-emerald-400" : "bg-slate-600"}`} /></button></td>
                    <td className="px-3 py-2 font-mono text-cyan-400">{f.chain}</td>
                    <td className="px-3 py-2 font-mono">{f.action}</td>
                    <td className="px-3 py-2 text-slate-400">{f.comment}</td>
                    <td className="px-3 py-2 text-right"><button data-testid={`wb-fw-del-${f.id}`} onClick={() => delFw(f.id)} className="p-1.5 rounded-md hover:bg-rose-500/10 text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button></td>
                  </tr>
                ))}
              </WBTable>
            </div>
          )}

          {section === "system" && (
            <div className="max-w-md space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("wb.identity")}</label>
                <div className="mt-1 flex gap-2">
                  <input data-testid="wb-identity-input" value={ident} onChange={(e) => setIdent(e.target.value)} className="flex-1 rounded-lg bg-[#111827] border border-[#1F2937] px-3 py-2 text-sm font-mono" />
                  <button data-testid="wb-identity-save" onClick={saveIdent} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 px-3 py-2 text-sm font-semibold"><Save className="h-4 w-4" /> {t("wb.save")}</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Info label={t("wb.version")} value={"RouterOS " + d.system.version} />
                <Info label={t("wb.board")} value={d.system.board_name} />
                <Info label="Architecture" value={d.system.architecture} />
                <Info label={t("wb.uptime")} value={d.system.uptime} />
              </div>
            </div>
          )}

          {section === "terminal" && (
            <div className="rounded-xl border border-[#1F2937] bg-black overflow-hidden">
              <div className="px-3 py-2 border-b border-[#1F2937] flex items-center gap-2 text-xs text-slate-400"><Terminal className="h-3.5 w-3.5" /> RouterOS Terminal — /system, /interface, /ip address, /ip hotspot active</div>
              <div ref={termRef} data-testid="wb-terminal-output" className="p-3 h-[380px] overflow-y-auto font-mono text-xs text-emerald-300 space-y-2">
                {term.length === 0 && <div className="text-slate-600">{t("wb.terminal_hint")}</div>}
                {term.map((l, i) => (
                  <div key={i}>
                    <div className="text-cyan-400">{l.p}</div>
                    <pre className="whitespace-pre-wrap text-emerald-300">{l.o}</pre>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 border-t border-[#1F2937] px-3 py-2">
                <span className="font-mono text-xs text-cyan-500">&gt;</span>
                <input data-testid="wb-terminal-input" value={cmd} onChange={(e) => setCmd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runCmd()} placeholder="/system resource print" className="flex-1 bg-transparent font-mono text-sm text-emerald-300 focus:outline-none" />
                <button data-testid="wb-terminal-run" onClick={runCmd} className="rounded-md bg-emerald-600/20 text-emerald-300 ring-1 ring-emerald-600/30 px-3 py-1 text-xs font-semibold">{t("wb.run")}</button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-5">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}
function Info({ label, value }) {
  return (
    <div className="rounded-lg bg-[#111827] border border-[#1F2937] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono text-sm text-cyan-400 truncate">{value}</div>
    </div>
  );
}
function WBTable({ headers, children }) {
  return (
    <div className="rounded-xl border border-[#1F2937] bg-[#111827] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#1F2937]">{headers.map((h, i) => <th key={i} className="px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}
